import {OrderSheetPageSet, OrderSheetPageSetType} from '@web/core/constants'
import getApi, {createWrapper, getBaseUrl, getCommonBaseUrl} from '@web/helpers/api'

import {
    GetPayLaterPayDueDayResponse,
    GetUserTradeableResponse,
    NotificationStatus,
    PayLaterMemberInfoResponse,
    PayLaterReloadResponse,
    PayLaterUserSignUpStatusResponse,
    UpdatePayLaterStatusParams,
    UpdatePayLaterStatusResponse,
} from './interfaces'

const PayLaterApiUrlInfo: Record<OrderSheetPageSetType, {reload: string; tooltipClosed?: string}> = {
    [OrderSheetPageSet.MALL]: {
        /** 후불결제 정보 재조회 */
        reload: '/orderSheet/mall/payLater',
    },
    [OrderSheetPageSet.EASYBOOKING]: {
        /** 후불결제 정보 재조회 */
        reload: '/orderSheet/easyBooking/payLater',
    },
    [OrderSheetPageSet.SELLER]: {
        /** 후불결제 정보 재조회 */
        reload: '/orderSheet/seller/payLater',
        /** 후불결제 튜토리얼 미노출 처리 */
        tooltipClosed: '/orderSheet/seller/paylater/tutorial/close',
    },
    // 패밀리 결제는 후불결제를 사용할 수 없습니다.
    [OrderSheetPageSet.FAMILY_PAY]: {
        reload: '',
        tooltipClosed: '',
    },
    // 클레임 비용결제는 후불결제는 사용할 수 없습니다.
    [OrderSheetPageSet.CLAIM]: {
        reload: '',
        tooltipClosed: '',
    },
}

/** 후불결제 정보 재조회 */
async function getPayLater(pageSet: OrderSheetPageSetType) {
    const url = PayLaterApiUrlInfo[pageSet].reload
    const baseUrl = getBaseUrl(pageSet)
    const api = getApi(baseUrl)

    return createWrapper<PayLaterReloadResponse>(api.get(url))
}

/** 후불결제 유저 상태 조회 (full) */
async function getPayLaterUserStatus(): Promise<PayLaterUserSignUpStatusResponse> {
    const commonBaseUrl = getCommonBaseUrl()
    const api = getApi(commonBaseUrl)
    return createWrapper<PayLaterUserSignUpStatusResponse>(api.get('/payLater/signUpStatus'))
}

/**
 * 후불결제 유저 상태 조회 (light)
 * 결제 시 납부일 변경 확인위해 추가
 **/
async function getLightPayLaterUserStatus(): Promise<PayLaterMemberInfoResponse> {
    const commonBaseUrl = getCommonBaseUrl()
    const api = getApi(commonBaseUrl)

    return createWrapper<PayLaterMemberInfoResponse>(api.get('/payLater/userStatus/light'))
}

/**
 * 후불결제 통합변경 API (납부일, 이메일, 납부계좌)
 */
async function updatePayLaterStatus({
    accountKey,
    email,
    newPayDue,
}: UpdatePayLaterStatusParams): Promise<UpdatePayLaterStatusResponse> {
    const commonBaseUrl = getCommonBaseUrl()
    const api = getApi(commonBaseUrl)

    return createWrapper<UpdatePayLaterStatusResponse>(
        api.post('/payLater/userStatus/info', {
            accountKey,
            email,
            newPayDue,
        }),
    )
}

/**
 * 후불결제 납부일 조회 API
 * @see https://wiki.fin.navercorp.com/pages/viewpage.action?pageId=68627217
 * 납부일을 15일만 관리하여 변경된 남부일 매개변수를 15일 고정으로 조회
 */
async function getPayLaterPayDueDay(): Promise<GetPayLaterPayDueDayResponse> {
    const commonBaseUrl = getCommonBaseUrl()
    const api = getApi(commonBaseUrl)

    return createWrapper<GetPayLaterPayDueDayResponse>(
        api.get('/payLater/payDue/info', {
            params: {
                newPayDue: 15,
            },
        }),
    )
}

/**
 * 후불결제 가입대상자 조회 API
 */
async function getCheckCandidate(): Promise<boolean> {
    const commonBaseUrl = getCommonBaseUrl()
    const api = getApi(commonBaseUrl)

    return createWrapper<boolean>(api.get('/payLater/userStatus/candidate'))
}

/**
 * 후불결제 가입미대상자 알림신청 조회 API
 */
async function getNotificationStatus(): Promise<NotificationStatus> {
    const commonBaseUrl = getCommonBaseUrl()
    const api = getApi(commonBaseUrl)

    return createWrapper<NotificationStatus>(api.get('/payLater/userStatus/notification'))
}

/**
 * 후불결제 가입미대상자 알림신청 변경 API
 */
async function updateNotificationStatus({enableYn}: NotificationStatus): Promise<NotificationStatus> {
    const commonBaseUrl = getCommonBaseUrl()
    const api = getApi(commonBaseUrl)

    return createWrapper<NotificationStatus>(
        api.post('/payLater/userStatus/notification', {
            enableYn,
        }),
    )
}

/**
 * 후불결제 거래 가능여부 조회 API
 */
async function getUserTradeable(): Promise<GetUserTradeableResponse> {
    const commonBaseUrl = getCommonBaseUrl()
    const api = getApi(commonBaseUrl)

    return createWrapper<GetUserTradeableResponse>(api.get('/payLater/userStatus/tradeable'))
}

export default {
    getPayLater,
    getPayLaterUserStatus,
    getLightPayLaterUserStatus,
    updatePayLaterStatus,
    getPayLaterPayDueDay,
    getCheckCandidate,
    getNotificationStatus,
    updateNotificationStatus,
    getUserTradeable,
}
